<script setup>
import { computed, defineModel, defineProps, onMounted, ref, watch } from 'vue';
import MultiSelectDropdown from '@/components/UI/MultiSelectDropdown.vue';
import SelectDropdown from '@/components/UI/SelectDropdown.vue';
import PlusSvg from '@/components/UI/svg/PlusSvg.vue';
import RefreshSvg from '@/components/UI/svg/RefreshSvg.vue';
import LoadingSvg from '@/components/UI/LoadingSvg.vue';
import BitrixDealConditionItem from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixDealConditionItem.vue';
import UploadDateRangeFields from '@/components/CreateConnectionComponents/UploadDateRangeFields.vue';
import { Api } from '@/service/api';
import { createToaster } from '@meforma/vue-toaster';

const props = defineProps({
    account: {
        type: [String, Object],
        default: null,
    },
    entityType: {
        type: String,
        default: 'DEALS',
    },
    title: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        default: '',
    },
    showDirections: {
        type: Boolean,
        default: true,
    },
    showStages: {
        type: Boolean,
        default: true,
    },
});

const directions = defineModel('directions', { default: () => [] });
const stages = defineModel('stages', { default: () => [] });
const conditionType = defineModel('conditionType', { default: 'AND' });
const conditions = defineModel('conditions', { default: () => [] });
const dateField = defineModel('dateField', { default: 'DATE_CREATE' });
const startDate = defineModel('startDate', {
    default: () => ({ type: 'exact', date: null }),
});
const endDate = defineModel('endDate', {
    default: () => ({ type: 'exact', date: null }),
});

const toaster = createToaster();

const entityLabel = computed(() => {
    if (props.entityType === 'LEADS') {
        return 'лиды';
    }
    return 'сделки';
});

const dateFieldOptions = computed(() => [
    {
        value: 'DATE_CREATE',
        label: 'Дата создания',
        description: `Фильтровать ${entityLabel.value} по дате создания`,
    },
    {
        value: 'DATE_MODIFY',
        label: 'Дата обновления',
        description: `Фильтровать ${entityLabel.value} по дате изменения`,
    },
]);

const dateRangeIdPrefix = computed(() => {
    return props.entityType === 'LEADS' ? 'bitrix-leads' : 'bitrix-deals';
});

const isSyncingDirections = ref(false);
const isSyncingStages = ref(false);
const isSyncingFields = ref(false);

const fallbackDirectionOptions = [
    { id: 'sales', name: 'Продажи' },
    { id: 'marketing', name: 'Маркетинг' },
    { id: 'support', name: 'Поддержка' },
];

const directionOptions = ref([...fallbackDirectionOptions]);
const stageOptions = ref([]);
const fieldOptions = ref([]);
const criteria = ref([]);

const conditionTypes = ref([
    {
        name: 'Данные соответствуют нескольким условиям одновременно (AND)',
        type: 'AND',
    },
    {
        name: 'Данные соответствуют одному из условий (OR)',
        type: 'OR',
    },
]);

const conditionTypeValue = computed({
    get: () => conditionTypes.value.find((item) => item.type === conditionType.value) || conditionTypes.value[0],
    set: (value) => {
        conditionType.value = value?.type || 'AND';
    },
});

const stageGridClass = computed(() => {
    return props.showDirections
        ? 'grid grid-cols-1 lg:grid-cols-2 gap-4'
        : 'grid grid-cols-1 gap-4';
});

const isSyncingAny = computed(() => {
    return isSyncingDirections.value || isSyncingStages.value || isSyncingFields.value;
});

const showDirectionsSync = computed(() => props.showDirections && props.entityType === 'DEALS');
const showStagesSync = computed(() => props.showStages && (props.entityType === 'DEALS' || props.entityType === 'LEADS'));
const showFieldsSync = computed(() => props.entityType === 'DEALS' || props.entityType === 'LEADS');

function normalizeOptions(items = []) {
    return items.map((item) => ({
        id: item.id ?? item.value ?? item.code,
        name: item.name ?? item.label ?? item.title,
    })).filter((item) => item.id != null && item.name);
}

function getAccountId() {
    if (!props.account) {
        return null;
    }

    if (typeof props.account === 'string' || typeof props.account === 'number') {
        return props.account;
    }

    return props.account.id || props.account.domain || props.account.portal || null;
}

function requireAccount({ silent = false } = {}) {
    const accountId = getAccountId();
    if (!accountId) {
        if (!silent) {
            toaster.error('Сначала выберите аккаунт Bitrix');
        }
        return null;
    }
    return accountId;
}

function clearInvalidSelections() {
    const directionIds = new Set(directionOptions.value.map((item) => item.id));
    const stageIds = new Set(stageOptions.value.map((item) => item.id));
    const fieldIds = new Set(fieldOptions.value.map((item) => item.id));

    directions.value = (directions.value || []).filter((item) => directionIds.has(item.id));
    stages.value = (stages.value || []).filter((item) => stageIds.has(item.id));

    conditions.value = (conditions.value || []).map((condition) => {
        const fieldId = condition.field?.id;
        if (fieldId && !fieldIds.has(fieldId)) {
            return {
                ...condition,
                field: {},
            };
        }
        return condition;
    });
}

function applyDirectionOptions(items) {
    const normalized = normalizeOptions(items);
    directionOptions.value = normalized.length
        ? normalized
        : [...fallbackDirectionOptions];
}

function applyStageOptions(items) {
    const normalized = normalizeOptions(items);
    if (normalized.length) {
        const uniqueById = new Map();
        normalized.forEach((item) => {
            uniqueById.set(String(item.id), item);
        });
        stageOptions.value = Array.from(uniqueById.values());
        return;
    }

    stageOptions.value = [];
}

function applyFieldOptions(items) {
    const normalized = normalizeOptions(items);
    fieldOptions.value = normalized.length ? normalized : [];
}

function getSelectedDirectionIds() {
    return (directions.value || [])
        .map((item) => item?.id)
        .filter((id) => id != null && id !== '');
}

async function syncCriteria({ silent = true } = {}) {
    try {
        const [criteriaResponse] = await Promise.allSettled([Api.getFilterCriteria()]);
        if (criteriaResponse.status === 'fulfilled') {
            criteria.value = criteriaResponse.value || [];
            return true;
        }
        if (!silent) {
            toaster.error('Не удалось синхронизировать критерии фильтра');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync filter criteria', error);
        if (!silent) {
            toaster.error('Не удалось синхронизировать критерии фильтра');
        }
        return false;
    }
}

async function syncDirections({ silent = false } = {}) {
    if (!showDirectionsSync.value || isSyncingDirections.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        directionOptions.value = [...fallbackDirectionOptions];
        clearInvalidSelections();
        return false;
    }

    isSyncingDirections.value = true;

    try {
        const [directionsResponse] = await Promise.allSettled([
            Api.getBitrixDealDirections(accountId),
        ]);

        if (directionsResponse.status === 'fulfilled' && Array.isArray(directionsResponse.value)) {
            applyDirectionOptions(directionsResponse.value);
            clearInvalidSelections();
            if (!silent) {
                toaster.success('Направления синхронизированы');
            }
            return true;
        }

        directionOptions.value = [...fallbackDirectionOptions];
        clearInvalidSelections();
        if (!silent) {
            toaster.error('Не удалось синхронизировать направления');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync Bitrix directions', error);
        directionOptions.value = [...fallbackDirectionOptions];
        if (!silent) {
            toaster.error('Не удалось синхронизировать направления');
        }
        return false;
    } finally {
        isSyncingDirections.value = false;
    }
}

async function syncDealStages({ domain, silent = false } = {}) {
    if (!(props.showStages && props.entityType === 'DEALS')) {
        return true;
    }

    const accountId = domain || getAccountId();
    const directionIds = getSelectedDirectionIds();

    if (!accountId || directionIds.length === 0) {
        stageOptions.value = [];
        stages.value = [];
        return true;
    }

    const stageResponses = await Promise.allSettled(
        directionIds.map((directionId) => Api.getBitrixDealStages(accountId, directionId)),
    );

    const mergedStages = stageResponses
        .filter((response) => response.status === 'fulfilled' && Array.isArray(response.value))
        .flatMap((response) => response.value);

    const hasSuccessfulResponse = stageResponses.some((response) => response.status === 'fulfilled');

    if (hasSuccessfulResponse) {
        applyStageOptions(mergedStages);
        return true;
    }

    stageOptions.value = [];
    if (!silent) {
        toaster.error('Не удалось синхронизировать стадии Bitrix');
    }
    return false;
}

async function syncLeadStages({ domain, silent = false } = {}) {
    if (!(props.showStages && props.entityType === 'LEADS')) {
        return true;
    }

    const accountId = domain || getAccountId();

    if (!accountId) {
        stageOptions.value = [];
        return true;
    }

    const [stagesResponse] = await Promise.allSettled([
        Api.getBitrixLeadStages(accountId),
    ]);

    if (stagesResponse.status === 'fulfilled' && Array.isArray(stagesResponse.value)) {
        applyStageOptions(stagesResponse.value);
        return true;
    }

    stageOptions.value = [];
    if (!silent) {
        toaster.error('Не удалось синхронизировать стадии Bitrix');
    }
    return false;
}

async function syncStages({ silent = false } = {}) {
    if (!showStagesSync.value || isSyncingStages.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        stageOptions.value = [];
        clearInvalidSelections();
        return false;
    }

    if (props.entityType === 'DEALS' && getSelectedDirectionIds().length === 0) {
        stageOptions.value = [];
        stages.value = [];
        if (!silent) {
            toaster.error('Сначала выберите направление');
        }
        return silent;
    }

    isSyncingStages.value = true;

    try {
        const synced = props.entityType === 'DEALS'
            ? await syncDealStages({ domain: accountId, silent })
            : await syncLeadStages({ domain: accountId, silent });

        clearInvalidSelections();

        if (synced && !silent) {
            toaster.success('Стадии синхронизированы');
        }

        return synced;
    } catch (error) {
        console.error('Failed to sync Bitrix stages', error);
        if (!silent) {
            toaster.error('Не удалось синхронизировать стадии Bitrix');
        }
        return false;
    } finally {
        isSyncingStages.value = false;
    }
}

async function syncFields({ silent = false } = {}) {
    if (!showFieldsSync.value || isSyncingFields.value) {
        return true;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        fieldOptions.value = [];
        clearInvalidSelections();
        return false;
    }

    isSyncingFields.value = true;

    try {
        await syncCriteria({ silent: true });

        const fieldsPromise = props.entityType === 'DEALS'
            ? Api.getBitrixDealFields(accountId)
            : Api.getBitrixLeadFields(accountId);

        const [fieldsResponse] = await Promise.allSettled([fieldsPromise]);

        if (fieldsResponse.status === 'fulfilled' && Array.isArray(fieldsResponse.value)) {
            applyFieldOptions(fieldsResponse.value);
            clearInvalidSelections();
            if (!silent) {
                toaster.success('Поля синхронизированы');
            }
            return true;
        }

        fieldOptions.value = [];
        clearInvalidSelections();
        if (!silent) {
            toaster.error('Не удалось синхронизировать поля Bitrix');
        }
        return false;
    } catch (error) {
        console.error('Failed to sync Bitrix fields', error);
        fieldOptions.value = [];
        if (!silent) {
            toaster.error('Не удалось синхронизировать поля Bitrix');
        }
        return false;
    } finally {
        isSyncingFields.value = false;
    }
}

async function syncFromBackend({ silent = false } = {}) {
    if (isSyncingAny.value) {
        return;
    }

    const accountId = requireAccount({ silent });
    if (!accountId) {
        if (showDirectionsSync.value) {
            directionOptions.value = [...fallbackDirectionOptions];
        }
        if (showStagesSync.value) {
            stageOptions.value = [];
        }
        if (showFieldsSync.value) {
            fieldOptions.value = [];
        }
        clearInvalidSelections();
        return;
    }

    const tasks = [];

    if (showDirectionsSync.value) {
        tasks.push(syncDirections({ silent: true }));
    }
    if (showFieldsSync.value) {
        tasks.push(syncFields({ silent: true }));
    }

    const results = await Promise.all(tasks);

    // Stages depend on selected directions for deals, so run after directions.
    let stagesSynced = true;
    if (showStagesSync.value) {
        stagesSynced = await syncStages({ silent: true });
    }

    const allSynced = results.every(Boolean) && stagesSynced;

    if (!silent) {
        if (allSynced) {
            toaster.success('Данные Bitrix синхронизированы');
        } else {
            toaster.error('Не удалось синхронизировать данные Bitrix');
        }
    }
}

watch(
    () => (directions.value || []).map((item) => item?.id).join(','),
    async () => {
        if (!(props.showStages && props.entityType === 'DEALS')) {
            return;
        }

        await syncStages({ silent: true });
        clearInvalidSelections();
    },
);

watch(
    () => getAccountId(),
    async (accountId, previousAccountId) => {
        if (!accountId || accountId === previousAccountId) {
            return;
        }

        await syncFromBackend({ silent: true });
    },
);

onMounted(async () => {
    if (!conditionType.value) {
        conditionType.value = 'AND';
    }

    if (!Array.isArray(conditions.value)) {
        conditions.value = [];
    }

    if (!Array.isArray(directions.value)) {
        directions.value = [];
    }

    if (!Array.isArray(stages.value)) {
        stages.value = [];
    }

    await syncFromBackend({ silent: true });
});

function addCondition() {
    conditions.value = [
        ...conditions.value,
        {
            id: Date.now() + conditions.value.length,
            field: {},
            criteria: {},
            filter_value: {
                type: '',
                data: '',
            },
        },
    ];
}

function removeCondition(condition) {
    conditions.value = conditions.value.filter((item) => item.id !== condition.id);
}
</script>

<template>
    <div class="w-full flex flex-col gap-6 px-6 pb-10">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
                <div class="text-base sm:text-lg font-bold text-gray-800 mb-3">
                    {{ title }}
                </div>
                <p
                    v-if="description"
                    class="text-sm text-gray-500"
                >
                    {{ description }}
                </p>
            </div>

            <button
                type="button"
                class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center justify-center self-start disabled:opacity-60 disabled:cursor-not-allowed"
                :disabled="isSyncingAny"
                @click="syncFromBackend()"
            >
                <LoadingSvg
                    v-if="isSyncingAny"
                    class="size-3 mr-2 animate-spin"
                />
                <RefreshSvg
                    v-else
                    class="size-4 mr-2 fill-blue-600"
                />
                <span>
                    Синхронизировать всё
                </span>
            </button>
        </div>

        <div
            v-if="showDirections || showStages"
            :class="stageGridClass"
        >
            <div
                v-if="showDirections"
                class="transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingDirections }"
            >
                <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="text-sm font-medium text-gray-700">
                        Выбор направления
                    </div>
                    <button
                        v-if="showDirectionsSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingDirections"
                        @click="syncDirections()"
                    >
                        <LoadingSvg
                            v-if="isSyncingDirections"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать</span>
                    </button>
                </div>
                <MultiSelectDropdown
                    v-model="directions"
                    :values="directionOptions"
                    label="Выберите направления"
                    :is-loading="isSyncingDirections"
                    is-loading-label="Синхронизация направлений..."
                    searchable
                    search-placeholder="Поиск направления..."
                />
            </div>

            <div
                v-if="showStages"
                class="transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingStages }"
            >
                <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="text-sm font-medium text-gray-700">
                        Выбор стадии
                    </div>
                    <button
                        v-if="showStagesSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingStages"
                        @click="syncStages()"
                    >
                        <LoadingSvg
                            v-if="isSyncingStages"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать</span>
                    </button>
                </div>
                <MultiSelectDropdown
                    v-model="stages"
                    :values="stageOptions"
                    label="Выберите стадии"
                    :is-loading="isSyncingStages"
                    is-loading-label="Синхронизация стадий..."
                    searchable
                    search-placeholder="Поиск стадии..."
                    show-select-all
                />
            </div>
        </div>

        <div class="w-full">
            <div class="text-sm font-medium text-gray-700 mb-3">
                Фильтровать по
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
                <button
                    v-for="option in dateFieldOptions"
                    :key="option.value"
                    type="button"
                    class="flex-1 px-4 py-3 rounded-md border-2 text-left transition-colors duration-150"
                    :class="dateField === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-blue-200 text-gray-700 hover:bg-blue-50/60'"
                    @click="dateField = option.value"
                >
                    <div class="text-sm font-semibold">
                        {{ option.label }}
                    </div>
                    <div class="text-xs mt-1 opacity-80">
                        {{ option.description }}
                    </div>
                </button>
            </div>

            <UploadDateRangeFields
                v-model:start-date="startDate"
                v-model:end-date="endDate"
                title="Период фильтрации"
                :id-prefix="dateRangeIdPrefix"
            />
        </div>

        <div class="w-full flex flex-col items-center">
            <SelectDropdown
                class="w-full"
                v-model="conditionTypeValue"
                :values="conditionTypes"
                label="Тип условия"
                :format-function="(item) => item.name"
                :is-error="false"
            />

            <div
                class="mt-5 w-full border border-black rounded-md flex flex-col items-center transition-opacity duration-200"
                :class="{ 'opacity-70': isSyncingFields }"
            >
                <div class="w-[calc(100%-20px)] mt-4 flex items-center justify-between gap-2">
                    <div class="text-sm font-medium text-gray-700">
                        Поля условий
                    </div>
                    <button
                        v-if="showFieldsSync"
                        type="button"
                        class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSyncingFields"
                        @click="syncFields()"
                    >
                        <LoadingSvg
                            v-if="isSyncingFields"
                            class="size-3 mr-1.5 animate-spin"
                        />
                        <RefreshSvg
                            v-else
                            class="size-3.5 mr-1.5 fill-blue-600"
                        />
                        <span>Синхронизировать поля</span>
                    </button>
                </div>

                <div
                    v-for="(condition, index) in conditions"
                    :key="condition.id"
                    class="w-[calc(100%-20px)] my-4"
                >
                    <BitrixDealConditionItem
                        v-model="conditions[index]"
                        :fields="fieldOptions"
                        :criteria="criteria"
                        :is-fields-loading="isSyncingFields"
                        @remove-condition="removeCondition(condition)"
                    />
                </div>

                <div
                    class="w-[calc(100%-40px)] flex items-center justify-center hover:bg-blue-50 my-4 rounded-md transition-all cursor-pointer duration-200"
                    @click="addCondition"
                >
                    <div class="flex items-center justify-center gap-3 py-4">
                        <PlusSvg class="size-6 stroke-2 stroke-blue-600" />
                        <div class="font-bold text-blue-600">
                            Добавить условие
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
